import type { MultiSelectOption, StatusIconBarItem, StatusName } from '@chatsdet/ui';
import { RUN_AREA_STATE_BADGE, RUN_AREA_STATES, type RunAreaDto, type RunAreaState } from './useExternalRun';

/**
 * An area's status square, keyed off `area.state` exhaustively so a new
 * state becomes a compile error here rather than silently falling through.
 *
 * `completed` still needs its own counts: the server's `deriveAreaState`
 * (`apps/api/src/external-tests/areas.ts`) returns `completed` only when no
 * scenario failed and the shard itself didn't fail, so a completed area
 * never has failures. But it can still have no pass or flaky results — e.g.
 * a sealed run whose shard reported nothing, or only skips — and that must
 * read as `skipped`, never as a green pass.
 */
export function areaToSummaryStatus(area: RunAreaDto): StatusIconBarItem['status'] {
  switch (area.state) {
    case 'running':
      return 'running';
    case 'queued':
      return 'queued';
    case 'canceled':
      return 'canceled';
    case 'failed':
      return 'failed';
    case 'disabled':
      return 'skipped';
    case 'completed':
      if (area.flaky > 0) return 'flaky_success';
      if (area.passed > 0) return 'success';
      return 'skipped';
  }
}

/**
 * The State cell's badge for an area — agrees with `areaToSummaryStatus`
 * above so the areas grid and the areas table never disagree about a
 * completed area with no results: `RUN_AREA_STATE_BADGE.completed` reads
 * "Passed"/success, which would be wrong for a shard that finished having
 * reported nothing (or only skips).
 */
export function areaStateBadge(area: RunAreaDto): { label: string; status: StatusName } {
  if (area.state === 'completed' && area.passed === 0 && area.flaky === 0) {
    return { label: 'No results', status: 'skipped' };
  }
  return RUN_AREA_STATE_BADGE[area.state];
}

/**
 * The tooltip message for an area's status square. While the area is still
 * running, state what's been observed so far rather than a claim that reads
 * as final — plus which retry attempt this is, when it isn't the first.
 */
export function areaSummaryMessage(area: RunAreaDto): string {
  if (area.state === 'disabled') return 'Turned off: not run';
  if (area.state === 'running') {
    const retrySuffix = area.attempt > 1 ? ` · retry ${area.attempt - 1}` : '';
    return `${area.passed} passed · ${area.failed} failed so far${retrySuffix}`;
  }
  return `${area.passed} passed · ${area.failed} failed`;
}

export type AreaFilter = {
  q: string;
  states: RunAreaState[];
};

/** Case-insensitive substring match over an area's label. */
function matchesQuery(area: RunAreaDto, q: string): boolean {
  if (!q.trim()) return true;
  return area.label.toLowerCase().includes(q.trim().toLowerCase());
}

function matchesStates(area: RunAreaDto, states: RunAreaState[]): boolean {
  if (states.length === 0) return true;
  return states.includes(area.state);
}

/** Client-side filter for the areas grid: free-text label search + state. */
export function filterAreas(areas: RunAreaDto[], filter: AreaFilter): RunAreaDto[] {
  return areas.filter((area) => matchesQuery(area, filter.q) && matchesStates(area, filter.states));
}

/**
 * Labels for the State filter itself — this filters on `area.state`, not on
 * outcome, so it must say the state word ("Completed") rather than
 * `RUN_AREA_STATE_BADGE`'s outcome word ("Passed"). Otherwise a "Passed (n)"
 * option (and its "State: Passed" chip) would include every completed area
 * whose table badge reads "No results" via `areaStateBadge` — the filter and
 * its own chip would be lying about what it actually selects.
 */
export const AREA_STATE_FILTER_LABEL: Record<RunAreaState, string> = {
  running: 'Running',
  queued: 'Queued',
  completed: 'Completed',
  failed: 'Failed',
  canceled: 'Canceled',
  disabled: 'Disabled',
};

/**
 * The states worth offering as a filter, in `RUN_AREA_STATES`'s fixed
 * display order, each labeled with how many areas currently hold that
 * state — mirrors `buildVerdictOptions` in `ExternalTestsTable`, but for
 * area state rather than scenario verdict.
 */
export function buildAreaStateOptions(areas: RunAreaDto[]): MultiSelectOption[] {
  const counts = new Map<RunAreaState, number>(RUN_AREA_STATES.map((state) => [state, 0]));
  for (const area of areas) counts.set(area.state, (counts.get(area.state) ?? 0) + 1);
  return RUN_AREA_STATES.filter((state) => (counts.get(state) ?? 0) > 0).map((state) => ({
    id: state,
    label: `${AREA_STATE_FILTER_LABEL[state]} (${counts.get(state) ?? 0})`,
  }));
}
